import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { X, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { fileUrl } from "@/components/ImageUpload";

const KEY = "bmm_popup_dismissed";
const EXCLUDED = ["/game", "/admin"];
let cached = null;
let fetchedAt = 0;
const REFRESH_MS = 5 * 60 * 1000;

export const POPUP_PAGES = [
  ["*", "All pages"], ["/", "Home"], ["/schedule", "Schedule"], ["/mustangs", "Mustangs"],
  ["/mustangs/*", "Horse profile pages"], ["/scores", "Scores"], ["/fan-favorite", "Fan Favorite"],
  ["/shop", "Shop"], ["/more", "More"], ["/about", "About"], ["/judges", "Judges"], ["/vendors", "Vendors"],
  ["/sponsors", "Sponsors"], ["/map", "Map"], ["/announcements", "Announcements"], ["/faq", "FAQ"],
];

const SENT = "bmm_popup_sent";
// Each event kind is counted once per browser per popup version.
const track = (version, kind) => {
  const k = `${version}:${kind}`;
  const sent = (localStorage.getItem(SENT) || "").split(",");
  if (sent.includes(k)) return;
  localStorage.setItem(SENT, [...sent.filter((x) => x.startsWith(`${version}:`)), k].join(","));
  api.post("/popup/event", { version, kind }).catch(() => {});
};

const matches = (pages, path) => pages.some((p) => p === "*" || p === path || (p.endsWith("/*") && path.startsWith(p.slice(0, -1))));

export const SitePopup = () => {
  const { pathname } = useLocation();
  const [popup, setPopup] = useState(cached);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(KEY));

  useEffect(() => {
    if (Date.now() - fetchedAt < REFRESH_MS) return;
    fetchedAt = Date.now();
    api.get("/popup").then((r) => { cached = r.data.popup; setPopup(cached); }).catch(() => { fetchedAt = 0; });
  }, [pathname]);

  const show = popup && dismissed !== popup.version && !EXCLUDED.some((x) => pathname.startsWith(x)) && matches(popup.pages || [], pathname)
    && (!popup.end_at || new Date() < new Date(popup.end_at));

  useEffect(() => {
    if (!show) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    track(popup.version, "view");
    return () => { document.body.style.overflow = prev; };
  }, [show, popup?.version]);

  if (!show) return null;
  const finish = (kind) => { track(popup.version, kind); localStorage.setItem(KEY, popup.version); setDismissed(popup.version); };
  return <PopupDialog popup={popup} onClose={() => finish("dismiss")} onCta={() => finish("click")} />;
};

const PopupDialog = ({ popup, onClose, onCta }) => {
  const external = /^https?:\/\//.test(popup.button_url || "");
  const btnCls = "flex h-11 flex-1 items-center justify-center gap-1.5 rounded-full bg-secondary px-4 text-sm font-bold text-white active:scale-95";
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" data-testid="site-popup">
      <div className="animate-fade-up flex max-h-[85vh] w-full max-w-md flex-col overflow-hidden rounded-2xl bg-card shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-border px-5 py-3">
          <h2 data-testid="site-popup-title" className="font-display text-xl font-bold leading-tight text-primary">{popup.title}</h2>
          <button data-testid="site-popup-x" onClick={onClose} aria-label="Close" className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-muted text-muted-foreground"><X size={18} /></button>
        </div>
        <div data-testid="site-popup-body" className="flex-1 overflow-y-auto overscroll-contain px-5 py-4">
          {popup.image && <img src={fileUrl(popup.image)} alt="" className="mb-3 w-full rounded-xl object-cover" />}
          {popup.body && <p className="whitespace-pre-line text-sm leading-relaxed text-foreground/85">{popup.body}</p>}
        </div>
        <div className="flex gap-2 border-t border-border bg-card px-5 py-3">
          {popup.button_url && popup.button_label && (external
            ? <a data-testid="site-popup-cta" href={popup.button_url} target="_blank" rel="noopener noreferrer" onClick={onCta} className={btnCls}>{popup.button_label} <ArrowRight size={15} /></a>
            : <Link data-testid="site-popup-cta" to={popup.button_url} onClick={onCta} className={btnCls}>{popup.button_label} <ArrowRight size={15} /></Link>)}
          <button data-testid="site-popup-dismiss" onClick={onClose} className="h-11 flex-1 rounded-full border border-border px-4 text-sm font-bold text-primary active:scale-95">Close</button>
        </div>
      </div>
    </div>
  );
};
